/**
 * Campos de formulario.
 *
 * Todos comparten tres cosas que no son cosméticas:
 *
 * - **La etiqueta está unida al control** con `htmlFor`, para que pulsar
 *   en el texto enfoque el campo y para que un lector de pantalla lo lea.
 * - **El error se anuncia**, no sólo se pinta en rojo: `aria-invalid` y
 *   `aria-describedby`.
 * - **La ayuda contextual explica la CONSECUENCIA**, no el formato
 *   (ERP-UX.md §8). En los campos con implicación legal, saber que un uso
 *   determina si aplica la inspección decenal cambia cómo se rellena.
 */

import { useId, type ReactNode } from 'react';

interface BaseProps {
  label: string;
  error?: string | undefined;
  help?: ReactNode;
  required?: boolean;
  disabled?: boolean;
}

interface FieldShellProps extends BaseProps {
  id: string;
  children: ReactNode;
  errorId: string;
  helpId: string;
}

function FieldShell({
  id,
  label,
  error,
  help,
  required,
  children,
  errorId,
  helpId,
}: FieldShellProps): ReactNode {
  return (
    <div className={`field${error ? ' field--error' : ''}`}>
      <label htmlFor={id}>
        {label}
        {!required && <span className="field__optional">opcional</span>}
      </label>
      {children}
      {help && (
        <p className="field__help" id={helpId}>
          {help}
        </p>
      )}
      {error && (
        <p className="field__error" id={errorId} role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

function describedBy(error: string | undefined, help: ReactNode, errorId: string, helpId: string) {
  const ids = [error ? errorId : null, help ? helpId : null].filter(Boolean);
  return ids.length > 0 ? ids.join(' ') : undefined;
}

// ── Texto ──────────────────────────────────────────────────────────

interface TextProps extends BaseProps {
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  type?: 'text' | 'email' | 'tel' | 'date' | 'number' | 'password';
  placeholder?: string;
  maxLength?: number;
  step?: string;
}

export function TextField({
  label,
  value,
  onChange,
  onBlur,
  error,
  help,
  required,
  disabled,
  type = 'text',
  placeholder,
  maxLength,
  step,
}: TextProps): ReactNode {
  const id = useId();
  const errorId = `${id}-error`;
  const helpId = `${id}-help`;

  return (
    <FieldShell
      id={id}
      label={label}
      {...(error !== undefined ? { error } : {})}
      help={help}
      {...(required !== undefined ? { required } : {})}
      errorId={errorId}
      helpId={helpId}
    >
      <input
        id={id}
        type={type}
        value={value}
        placeholder={placeholder ?? ''}
        maxLength={maxLength ?? undefined}
        step={step ?? undefined}
        disabled={disabled ?? false}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy(error, help, errorId, helpId)}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur ?? undefined}
      />
    </FieldShell>
  );
}

// ── Área de texto ──────────────────────────────────────────────────

interface AreaProps extends BaseProps {
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  rows?: number;
}

export function TextArea({
  label,
  value,
  onChange,
  onBlur,
  error,
  help,
  required,
  disabled,
  rows = 3,
}: AreaProps): ReactNode {
  const id = useId();
  const errorId = `${id}-error`;
  const helpId = `${id}-help`;

  return (
    <FieldShell
      id={id}
      label={label}
      {...(error !== undefined ? { error } : {})}
      help={help}
      {...(required !== undefined ? { required } : {})}
      errorId={errorId}
      helpId={helpId}
    >
      <textarea
        id={id}
        rows={rows}
        value={value}
        disabled={disabled ?? false}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy(error, help, errorId, helpId)}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur ?? undefined}
      />
    </FieldShell>
  );
}

// ── Selección ──────────────────────────────────────────────────────

interface SelectProps extends BaseProps {
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  options: ReadonlyArray<{ value: string; label: string }>;
  /** Texto de la opción vacía. Sin él, el campo no se puede dejar en blanco. */
  emptyLabel?: string;
}

export function SelectField({
  label,
  value,
  onChange,
  onBlur,
  options,
  emptyLabel,
  error,
  help,
  required,
  disabled,
}: SelectProps): ReactNode {
  const id = useId();
  const errorId = `${id}-error`;
  const helpId = `${id}-help`;

  return (
    <FieldShell
      id={id}
      label={label}
      {...(error !== undefined ? { error } : {})}
      help={help}
      {...(required !== undefined ? { required } : {})}
      errorId={errorId}
      helpId={helpId}
    >
      <select
        id={id}
        value={value}
        disabled={disabled ?? false}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy(error, help, errorId, helpId)}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur ?? undefined}
      >
        {emptyLabel !== undefined && <option value="">{emptyLabel}</option>}
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </FieldShell>
  );
}

// ── Casilla ────────────────────────────────────────────────────────

interface CheckProps extends Omit<BaseProps, 'required'> {
  checked: boolean;
  onChange: (checked: boolean) => void;
}

export function CheckField({ label, checked, onChange, help, error, disabled }: CheckProps): ReactNode {
  const id = useId();
  const helpId = `${id}-help`;

  return (
    <div className="field field--check">
      <label htmlFor={id}>
        <input
          id={id}
          type="checkbox"
          checked={checked}
          disabled={disabled ?? false}
          aria-describedby={help ? helpId : undefined}
          onChange={(e) => onChange(e.target.checked)}
        />
        {label}
      </label>
      {help && (
        <p className="field__help" id={helpId}>
          {help}
        </p>
      )}
      {error && (
        <p className="field__error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

// ── Estructura ─────────────────────────────────────────────────────

/**
 * Sección de un formulario largo.
 *
 * ERP-UX.md §4 pide páginas con secciones ancladas para los formularios
 * largos, en lugar de un modal que obliga a hacer scroll dentro de una
 * ventana dentro de la página.
 */
export function FormSection({
  title,
  description,
  children,
}: {
  title: string;
  description?: ReactNode;
  children: ReactNode;
}): ReactNode {
  return (
    <section className="form__section">
      <h2 className="form__section-title">{title}</h2>
      {description && <p className="form__section-note">{description}</p>}
      <div className="form__grid">{children}</div>
    </section>
  );
}
